import {useEffect, useState} from 'react';
import {
    Link,
    useHistory,
    useLocation
} from "react-router-dom";

import '../../assets/styles/account/account.scss';
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {useAuth} from "../../services/use-auth";
import { useMetaTags } from '../../services/useMetaTags';
import trackPageView from "../../services/trackPageView";

const EMAIL_OR_PHONE_REGEX = /^((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))|((.+)?([1-9][0-9][0-9]{7}))$/;

function isUnsafePostLoginTarget(url) {
    if (!url || typeof url !== 'string') {
        return true;
    }

    if (typeof window === 'undefined' || !window.location?.origin) {
        return /^\/api(?:\/|$)/.test(url) || url === '/account/login';
    }

    try {
        const parsed = new URL(url, window.location.origin);

        if (parsed.origin !== window.location.origin) {
            return false;
        }

        return /^\/api(?:\/|$)/.test(parsed.pathname) || parsed.pathname === '/account/login';
    } catch (e) {
        return true;
    }
}

function navigateToTargetUrl(url, history) {
    if (!url || typeof url !== 'string') {
        history.replace('/');
        return;
    }

    if (typeof window === 'undefined' || !window.location?.origin) {
        history.replace(url);
        return;
    }

    try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.origin === window.location.origin) {
            history.replace(`${parsed.pathname}${parsed.search}${parsed.hash}`);
            return;
        }
    } catch (e) {
        history.replace(url);
        return;
    }

    window.location.href = url;
}

export default function Login() {
    const { t } = useTranslation();
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    useMetaTags({
        title: t('app.title'),
        description: t('app.description'),
        metas: [
            { name: 'keywords', content: t('app.keywords') }
        ],
    });
    useEffect(() => {
        trackPageView(t('app.title'));
    }, [t]);

    const schema = yup.object().shape({
        username: yup.string()
            .required(t('login.errors.required'))
            .matches(EMAIL_OR_PHONE_REGEX, {message: t('login.errors.emailOrPhone')}),
        password: yup.string()
            .required(t('login.errors.required'))
    });
    const {register, handleSubmit, errors} = useForm(
        {
            resolver: yupResolver(schema),
            mode: 'onBlur',
            reValidateMode: 'onBlur',
        }
    );
    const [showPassword, setShowPassword] = useState(false);
    const [loginErrors, setLoginErrors] = useState({});

    const onSubmit = (data) => {
        setLoginErrors({});
        const normalizedUsername = (data.username || '').replace(/[()\s]+/g, '');
        const stateFrom = location.state && location.state.from ? location.state.from : null;
        const fallbackUrl = stateFrom ? `${stateFrom.pathname || '/'}${stateFrom.search || ''}${stateFrom.hash || ''}` : '';
        auth.signIn(
            normalizedUsername,
            data.password,
            (result) => {
                const backendTargetUrl = result?.data?.url || '';
                const safeBackendTargetUrl = isUnsafePostLoginTarget(backendTargetUrl) ? '' : backendTargetUrl;
                const targetUrl = fallbackUrl || safeBackendTargetUrl || '/';
                navigateToTargetUrl(targetUrl, history);
            },
            setLoginErrors
        );
    };

    return (
        <section className="section flex-center">
            <div className="authentication-form login-form login-form--modern">
                <div className="login-head">
                    <h1>{t('login.header')}</h1>
                    <p className="login-subtitle">{t('login.subtitle')}</p>
                </div>

                <div className="login-socials">
                    <button
                        className="login-socials__button login-socials__button--google"
                        type="button"
                        onClick={() => window.location.href = '/connect/google'}
                    >
                        <span className="login-socials__icon login-socials__icon--google" aria-hidden="true">G</span>
                        {t('buttons.googleLogin')}
                    </button>

                    <button
                        className="login-socials__button login-socials__button--facebook"
                        type="button"
                        onClick={() => window.location.href = '/connect/facebook'}
                    >
                        <span className="login-socials__icon login-socials__icon--facebook" aria-hidden="true">f</span>
                        {t('buttons.facebookLogin')}
                    </button>
                </div>

                <div className="login-divider">
                    <span>{t('login.divider')}</span>
                </div>

                <form className="login-shell" onSubmit={handleSubmit(onSubmit)} id="login" noValidate>
                    <div className="login-field">
                        <label className="login-field__label" htmlFor="username">
                            {t('login.labels.username')}
                        </label>
                        <div className={`login-field__control${errors.username ? ' is-error' : ''}`}>
                            <input
                                type="text"
                                name="username"
                                id="username"
                                ref={register}
                                className="login-field__input"
                                autoComplete="username"
                                placeholder={t('login.labels.usernamePlaceholder')}
                            />
                        </div>
                        { errors.username && <span className="login-field__error">{errors.username.message}</span> }
                    </div>

                    <div className="login-field">
                        <div className="login-field__row">
                            <label className="login-field__label" htmlFor="password">
                                {t('login.labels.password')}
                            </label>
                            <Link to="/account/restore-password" className="login-field__inline-link">
                                {t('login.password.forgot')}
                            </Link>
                        </div>
                        <div className={`login-field__control login-field__control--with-action${errors.password ? ' is-error' : ''}`}>
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                id="password"
                                ref={register}
                                className="login-field__input"
                                autoComplete="current-password"
                                placeholder={t('login.labels.passwordPlaceholder')}
                            />
                            <button
                                type="button"
                                className="login-field__action"
                                onClick={() => setShowPassword((current) => !current)}
                            >
                                { showPassword ? t('login.password.hide') : t('login.password.show') }
                            </button>
                        </div>
                        { errors.password && <span className="login-field__error">{errors.password.message}</span> }
                    </div>

                    { loginErrors.errors && <div className="login-submit-error">
                        <div className="login-submit-error__text">
                            {t('login.errors.incorrectData')}
                        </div>
                    </div> }

                    <div className="submit-block">
                        <button type="submit" className="login-submit">
                            {t('login.login')}
                        </button>
                    </div>

                    <div className="login-footer js-login-form-registration-link">
                        <span>{t('login.footer')}</span>
                        <Link rel="registration-form" to="/account/registration"
                           className="login-footer__link js-link-to-form">{t('buttons.registration')}</Link>
                    </div>
                </form>
            </div>
        </section>
    )
}
